import React, { useRef } from 'react';
import { mapSceneDataToRoomLayout } from '../../../data-transformer/index.ts';
import type { RoomLayout, Questionnaire } from '../../../data-transformer/index.ts';
import { styles } from '../styles.ts';

interface ConfigFormProps {
  roomData: RoomLayout;
  questionnaire: Questionnaire;
  handleFieldChange: (field: keyof RoomLayout, value: any) => void;
  handleQuestionnaireChange: (field: keyof Questionnaire, value: any) => void;
  // DB integration props
  dbRooms: Array<{ id: string; name: string; isEmpty: boolean }>;
  selectedRoomId: string;
  handleRoomSelect: (roomId: string) => void;
  onRoomDataChange: (data: RoomLayout) => void;
}

export function ConfigForm({
  roomData,
  questionnaire,
  handleFieldChange,
  handleQuestionnaireChange,
  dbRooms,
  selectedRoomId,
  handleRoomSelect,
  onRoomDataChange
}: ConfigFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const rawContent = event.target?.result as string;
        const parsed = JSON.parse(rawContent);
        
        let roomDataToUse: RoomLayout;
        
        // Detect if this is standard SceneData (has _rooms or _version) or RoomLayout
        if (parsed && (parsed._rooms || parsed._version)) {
          roomDataToUse = mapSceneDataToRoomLayout(parsed);
        } else {
          if (!parsed.id || !parsed.name || typeof parsed.width !== 'number' || !Array.isArray(parsed.items)) {
            alert('Invalid room layout JSON structure. The JSON must contain at least "id", "name", "width", "length", "height", and "items".');
            return;
          }
          roomDataToUse = parsed as RoomLayout;
        }
        
        onRoomDataChange(roomDataToUse);
      } catch (err) {
        alert('Failed to parse JSON file: ' + (err instanceof Error ? err.message : String(err)));
      }
    };
    reader.readAsText(file);
  };

  return (
    <section style={styles.card}>
      <h2 style={styles.sectionHeader}>Room Configuration Settings</h2>

      {/* Select Room source and upload in one row */}
      <div style={styles.row}>
        {/* Room Selector from DB */}
        <div style={{ ...styles.col, flex: 2 }}>
          <label style={styles.fieldLabel}>Select Room Source:</label>
          <select
            value={selectedRoomId}
            onChange={(e) => handleRoomSelect(e.target.value)}
            style={styles.select}
          >
            <option value="empty">-- Empty Room (5x5m) --</option>
            {dbRooms.map((room) => (
              <option key={room.id} value={room.id}>
                {room.name} {room.isEmpty ? ' [Empty]' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Upload room from file JSON */}
        <div style={{ ...styles.col, flex: 2 }}>
          <label style={styles.fieldLabel}>Upload Room Layout (JSON):</label>
          <div style={styles.fileUploadRow}>
            <input 
              type="file" 
              accept=".json" 
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <button 
              type="button" 
              onClick={() => fileInputRef.current?.click()} 
              style={styles.fileUploadBtn}
            >
              Choose JSON File
            </button>
            <span style={styles.fileUploadText}>
              {roomData.id && roomData.id !== 'room-empty' && roomData.id !== 'room-office' && roomData.id !== 'room-living' 
                ? `Active: ${roomData.name || roomData.id}` 
                : 'No custom file loaded'}
            </span>
          </div>
        </div>
      </div>

      {/* Room dimensions inputs */}
      <div style={styles.row}>
        <div style={styles.col}>
          <label style={styles.fieldLabel}>Width (m)</label>
          <input 
            type="number" 
            step="0.1" 
            value={roomData.width} 
            onChange={(e) => handleFieldChange('width', parseFloat(e.target.value) || 0)} 
            style={styles.input} 
          />
        </div>
        <div style={styles.col}>
          <label style={styles.fieldLabel}>Length (m)</label>
          <input 
            type="number" 
            step="0.1" 
            value={roomData.length} 
            onChange={(e) => handleFieldChange('length', parseFloat(e.target.value) || 0)} 
            style={styles.input} 
          />
        </div>
        <div style={styles.col}>
          <label style={styles.fieldLabel}>Height (m)</label>
          <input 
            type="number" 
            step="0.1" 
            value={roomData.height} 
            onChange={(e) => handleFieldChange('height', parseFloat(e.target.value) || 0)} 
            style={styles.input} 
          />
        </div>
      </div>

      {/* Room occupancy inputs */}
      <div style={styles.formGroup}>
        <label style={styles.fieldLabel}>Occupancy Preferences (Questionnaire):</label>
        <div style={styles.row}>
          <div style={styles.col}>
            <span style={styles.numberLabel}>Adults</span>
            <input 
              type="number" 
              value={questionnaire.numberOfAdults} 
              onChange={(e) => handleQuestionnaireChange('numberOfAdults', parseInt(e.target.value) || 0)} 
              style={styles.input} 
            />
          </div>
          <div style={styles.col}>
            <span style={styles.numberLabel}>Children</span>
            <input 
              type="number" 
              value={questionnaire.numberOfChildren} 
              onChange={(e) => handleQuestionnaireChange('numberOfChildren', parseInt(e.target.value) || 0)} 
              style={styles.input} 
            />
          </div>
          <div style={styles.col}>
            <span style={styles.numberLabel}>Guests</span>
            <input 
              type="number" 
              value={questionnaire.numberOfGuests} 
              onChange={(e) => handleQuestionnaireChange('numberOfGuests', parseInt(e.target.value) || 0)} 
              style={styles.input} 
            />
          </div>
        </div>
      </div>

      {/* Questionnaire toggle inclusions */}
      <div style={styles.formGroup}>
        <label style={styles.fieldLabel}>Zone Inclusions:</label>
        <div style={styles.checkboxContainer}>
          <label style={styles.checkboxLabel}>
            <input 
              type="checkbox" 
              checked={questionnaire.workplace} 
              onChange={(e) => handleQuestionnaireChange('workplace', e.target.checked)} 
              style={styles.checkbox}
            />
            Include Workspace Zone
          </label>
          <label style={styles.checkboxLabel}>
            <input 
              type="checkbox" 
              checked={questionnaire.playground} 
              onChange={(e) => handleQuestionnaireChange('playground', e.target.checked)} 
              style={styles.checkbox}
            />
            Include Kids Playground
          </label>
        </div>
      </div>
    </section>
  );
}
